import React, { useCallback, useMemo, useState } from 'react';
import { PropTypes } from 'prop-types';
import { Link } from 'react-router-dom';

import { parseDate, parseFullName, parseSum } from 'utils/helpers';
import {
    RoleNames,
    subscriptionStatus as SubscriptionStatus,
    investorClassificationKeys,
    promoterRoles,
    subscriptionStatus,
} from 'utils/constants';
import { useMatchAtLeastOneRole } from 'utils/hooks';
import { texts } from 'utils/languages/fr';

import UserStateIndicator from './UserStateIndicator';
import { ActionsMenu, SMSDialog, StyledTableRow } from 'components';
import {
    MenuDetails,
    MenuPortfolio,
    MenuSendMessage,
    MenuSendSMS,
    MenuSubstitute,
} from 'components/ActionsMenu/actions';
import { TableCell, Typography } from '@mui/material';

const ImpersonateItem = React.forwardRef((props, ref) => <MenuSubstitute ref={ref} {...props} />);
const MessageItem = React.forwardRef((props, ref) => <MenuSendMessage ref={ref} {...props} />);
const SMSItem = React.forwardRef((props, ref) => <MenuSendSMS ref={ref} {...props} />);

const InvestorAccountRow = ({ investor }) => {
    const userIsDistributor = useMatchAtLeastOneRole([
        RoleNames.ADMIN_CIF,
        RoleNames.COMMERCIALISATEUR,
        RoleNames.PSI,
    ]);
    const isAdminOrSignatory = useMatchAtLeastOneRole([
        RoleNames.SERVICER_ADMIN,
        RoleNames.SERVICER_SIGNATORY,
    ]);
    const userIsStandardServicer = useMatchAtLeastOneRole([RoleNames.SERVICER_STANDARD]);
    const userIsPromoter = useMatchAtLeastOneRole(promoterRoles);

    const firstSubscription = useMemo(() => investor.subscriptions?.[0], [investor]);
    const createdAt = useMemo(() => parseDate(firstSubscription?.createdAt), [firstSubscription]);
    const profile = useMemo(
        () => (firstSubscription.role?.name?.includes('PP') ? 'PP' : 'PM'),
        [firstSubscription]
    );
    const finishedSubscriptions = useMemo(
        () =>
            investor.subscriptions.filter(
                s => s.subscriptionStatus === subscriptionStatus.FINISHED
            ),
        [investor]
    );
    const investorStatus = investor.subscriptions.reduce(
        (isFinished, subscription) =>
            isFinished || subscription.subscriptionStatus === SubscriptionStatus.FINISHED,
        false
    );
    const hasOngoingSubscription = investor.subscriptions.reduce(
        (isOngoing, subscription) =>
            isOngoing || subscription.subscriptionStatus === SubscriptionStatus.IN_PROGRESS,
        false
    );

    const [smsOpen, setSmsOpen] = useState(false);

    const handleSMSOpen = useCallback(() => setSmsOpen(true), []);

    return (
        <StyledTableRow>
            <TableCell align="center">
                <UserStateIndicator
                    alertType={investorStatus ? '' : 'warning'}
                ></UserStateIndicator>
            </TableCell>
            <TableCell>
                {profile === 'PP' ? (
                    <React.Fragment>
                        <Typography gutterBottom fontSize="inherit">
                            {parseFullName(investor)}
                        </Typography>
                        {investor?.subscriptions?.[0].coSubscriber ? (
                            <Typography fontSize="inherit">
                                {parseFullName(investor?.subscriptions?.[0].coSubscriber)}
                            </Typography>
                        ) : (
                            ''
                        )}
                    </React.Fragment>
                ) : (
                    investor?.investorCompany?.corporateName
                )}
            </TableCell>
            <TableCell>
                {investor.investorClassification === investorClassificationKeys.PRO
                    ? texts.PRO_CLIENT
                    : texts.NON_PRO_CLIENT}
            </TableCell>
            <TableCell>{createdAt}</TableCell>
            <TableCell>
                {investor.subscriptions.map(s => (
                    <div key={s.id}>
                        <Link
                            to={`/subscriptions/details/${investor.id}/${s.id}`}
                            style={{ color: '#626262', textDecoration: 'none' }}
                        >
                            <span>
                                <UserStateIndicator
                                    alertType={
                                        s.subscriptionStatus === SubscriptionStatus.FINISHED
                                            ? ''
                                            : 'danger'
                                    }
                                />
                            </span>
                            &nbsp;
                            <span>{s.investmentFund?.name},</span>
                            &nbsp;
                            <span>{s.investmentCharacteristics?.shareSubcategory?.name},</span>
                            &nbsp;
                            <b>{parseSum(s?.investmentCharacteristics?.investingSum)}</b>
                        </Link>
                    </div>
                ))}
            </TableCell>
            {!userIsStandardServicer && (
                <TableCell>
                    {userIsPromoter ? (
                        finishedSubscriptions.length > 0 && (
                            <ActionsMenu autoClose>
                                <MenuPortfolio investor={investor} />
                            </ActionsMenu>
                        )
                    ) : (
                        <ActionsMenu autoClose>
                            {isAdminOrSignatory && (
                                <ImpersonateItem
                                    user={investor}
                                    originalPath="/investor-accounts"
                                    connectAs="subscriber"
                                    followupPath={
                                        userIsDistributor && investor?.subscriptions?.length > 1
                                            ? '/new-subscription'
                                            : null
                                    }
                                    itemText={texts.IMPERSONATE_SUBSCRIBER}
                                />
                            )}
                            {userIsDistributor && hasOngoingSubscription && (
                                <ImpersonateItem
                                    originalPath="/investor-accounts"
                                    user={investor}
                                    itemText={texts.CONTINUE_SUBSCRIPTION}
                                />
                            )}
                            <MessageItem receiver={investor} />
                            <SMSItem
                                disabled={!investor.phoneNumber}
                                onSendSMS={handleSMSOpen}
                                id={investor.id}
                            />
                            <MenuDetails path={`details/${investor.id}`} />
                            {userIsDistributor && finishedSubscriptions.length > 0 && (
                                <MenuPortfolio investor={investor} />
                            )}
                        </ActionsMenu>
                    )}
                </TableCell>
            )}
            <SMSDialog open={smsOpen} handleClose={() => setSmsOpen(false)} receiver={investor} />
        </StyledTableRow>
    );
};

InvestorAccountRow.propTypes = {
    investor: PropTypes.object,
};
export default InvestorAccountRow;
